import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useLanguage } from "../../context/Language/LanguageContext";
import AIReportChart, {
    chartFromPreNode,
    chartSpecFromTableNode,
} from "./AIReportChart";

const answerComponents = {
  h1: ({ node, ...props }) => (
    <h1 className="text-base font-bold mt-2 mb-1" {...props} />
  ),
  h2: ({ node, ...props }) => (
    <h2 className="text-sm font-bold mt-2 mb-1" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="text-sm font-semibold mt-2 mb-1" {...props} />
  ),
  p: ({ node, ...props }) => <p className="mb-2" {...props} />,
  ul: ({ node, ...props }) => (
    <ul className="list-disc ml-4 mb-2 space-y-0.5" {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol className="list-decimal ml-4 mb-2 space-y-0.5" {...props} />
  ),
  strong: ({ node, ...props }) => <strong className="font-bold" {...props} />,
  em: ({ node, ...props }) => <em className="italic" {...props} />,
  table: ({ node, ...props }) => {
    // Render numeric tables as a chart, matching the main report.
    const spec = chartSpecFromTableNode(node);
    if (spec) {
      return <AIReportChart spec={spec} />;
    }
    return (
      <table className="table-auto border-collapse my-2 text-[11px]" {...props} />
    );
  },
  th: ({ node, ...props }) => (
    <th className="border px-2 py-1 text-left" {...props} />
  ),
  td: ({ node, ...props }) => <td className="border px-2 py-1" {...props} />,
  pre: ({ node, children, ...props }) => {
    // Render chart blocks as a chart, matching the main report.
    const chart = chartFromPreNode(node);
    if (chart) {
      return <AIReportChart spec={chart.text} />;
    }
    return (
      <pre className="my-2 overflow-x-auto rounded bg-base-200 p-2 text-[10px]" {...props}>
        {children}
      </pre>
    );
  },
};

// Optional follow-up Q&A for an AI report. Users type their own questions,
// which are answered using the generated report as context. Asked questions
// are persisted to localStorage (under `storageKey`) so they can be re-asked
// later. The answered Q&A list renders inside the report container so it is
// included in the PDF output; the input controls are hidden when printing.
function FollowUpQuestions({
  report,
  callModel,
  systemPrompt,
  language,
  storageKey,
  disabled,
  translateTo,
  translateNonce,
}) {
  const { t } = useLanguage();
  const [question, setQuestion] = useState("");
  const [savedQuestions, setSavedQuestions] = useState([]);
  const [qa, setQa] = useState([]);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) || "[]");
      if (Array.isArray(stored)) {
        setSavedQuestions(stored);
      }
    } catch (e) {
      // ignore malformed storage
    }
  }, [storageKey]);

  const persist = (list) => {
    setSavedQuestions(list);
    try {
      localStorage.setItem(storageKey, JSON.stringify(list));
    } catch (e) {
      // ignore quota errors
    }
  };

  const removeSaved = (q) => {
    persist(savedQuestions.filter((item) => item !== q));
  };

  // Translate every answered follow-up question and answer into `translateTo`.
  // Triggered by the parent (via `translateNonce`) when the report is
  // translated, so the whole report including Q&A stays in one language.
  const translateAll = async (lang) => {
    if (!lang || qa.length === 0 || asking) {
      return;
    }
    setAsking(true);
    setError(null);
    try {
      const translated = await Promise.all(
        qa.map(async (item) => {
          const [tq, ta] = await Promise.all([
            callModel([
              {
                role: "system",
                content: `You are a professional translator. Translate the text into ${lang}. Only output the translation, with no quotes or extra text.`,
              },
              { role: "user", content: item.question },
            ]),
            callModel([
              {
                role: "system",
                content: `You are a professional translator. Translate the following text into ${lang}. Preserve Markdown structure, headings, and bullet points. Only output the translation.`,
              },
              { role: "user", content: item.answer },
            ]),
          ]);
          return {
            question: tq || item.question,
            answer: ta || item.answer,
          };
        })
      );
      setQa(translated);
    } catch (e) {
      setError(e.message || "Failed to translate follow-up questions.");
    } finally {
      setAsking(false);
    }
  };

  useEffect(() => {
    if (translateNonce && translateTo) {
      translateAll(translateTo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translateNonce]);

  const ask = async (q) => {
    const text = (q || "").trim();
    if (!text || asking || disabled) {
      return;
    }
    setAsking(true);
    setError(null);
    try {
      const messages = [
        { role: "system", content: systemPrompt },
        {
          role: "user",
          content:
            "Here is the analysis report you produced:\n\n" + (report || ""),
        },
        {
          role: "user",
          content:
            "Answer this follow-up question using only the data and analysis above. " +
            "If the data does not support an answer, say so briefly.\n\nQuestion: " +
            text +
            (language && language !== "English"
              ? `\n\nAnswer in ${language}.`
              : ""),
        },
      ];
      const answer = await callModel(messages);
      setQa((prev) => [
        ...prev,
        { question: text, answer: answer || "No response was returned." },
      ]);
      if (!savedQuestions.includes(text)) {
        persist([...savedQuestions, text]);
      }
      setQuestion("");
    } catch (e) {
      setError(e.message || "Failed to answer the question.");
    } finally {
      setAsking(false);
    }
  };

  return (
    <div className="mt-4">
      {qa.length > 0 && (
        <div className="border-t pt-2">
          <h2 className="text-sm font-bold mb-2">{t("Follow-up questions")}</h2>
          {qa.map((item, idx) => (
            <div key={idx} className="mb-3 break-inside-avoid">
              <p className="font-semibold">{t("Q:")} {item.question}</p>
              <div className="ml-2">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={answerComponents}
                >
                  {item.answer}
                </ReactMarkdown>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="print:hidden flex flex-col gap-2 mt-2">
        <label className="block text-xs font-medium text-base-content/70">
          {t("Ask a follow-up question")}
        </label>
        <div className="flex gap-1">
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                ask(question);
              }
            }}
            placeholder={t("e.g. Which rotation should we target on serve?")}
            className="input input-sm input-bordered w-full"
            disabled={disabled}
          />
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => ask(question)}
            disabled={asking || disabled || !question.trim()}
          >
            {asking ? (
              <span className="loading loading-spinner loading-xs"></span>
            ) : (
              t("Ask")
            )}
          </button>
        </div>

        {error && (
          <div className="alert alert-error text-xs p-2">
            <span>{error}</span>
          </div>
        )}

        {savedQuestions.length > 0 && (
          <div className="flex flex-wrap items-center gap-1">
            <span className="text-[10px] text-base-content/50 mr-1">
              {t("Saved questions:")}
            </span>
            {savedQuestions.map((q, i) => (
              <span
                key={i}
                className="badge badge-sm badge-outline gap-1 cursor-pointer"
                onClick={() => ask(q)}
                title={t("Click to ask again")}
              >
                {q}
                <button
                  type="button"
                  className="text-error"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSaved(q);
                  }}
                  title={t("Remove saved question")}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default FollowUpQuestions;
